import { useCallback, useEffect, useRef, useState } from 'react';
import { isAfter, subSeconds } from 'date-fns';
import type { UserApiId } from '@/model/User.model';
import { type AvatarColor, avatarSwatch, pickFarthestAvatarColorIndex } from '@/constants/colors';
import { useDataTable } from '@/components/UI/DataTableV2/DataTableProvider';
import { type DataTableActiveUsersChangeEvent, DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';

const ACTIVE_WINDOW_SECONDS = 60;
const IDLE_REEVALUATION_INTERVAL_MS = 10_000;

export interface OnlineUser {
  apiId: UserApiId;
  color: AvatarColor;
  isActive: boolean;
}

export interface OnlineUsersModel {
  /** Online users keyed by api id, in server order (most-recently-engaged first). */
  byId: ReadonlyMap<UserApiId, OnlineUser>;
  /** Api ids of users whose `last_active` is within the last 60 seconds. */
  activeUserIds: ReadonlySet<UserApiId>;
}

interface OnlineUserSnapshot {
  apiId: UserApiId;
  lastActive: string;
}

const EMPTY_MODEL: OnlineUsersModel = {
  byId: new Map(),
  activeUserIds: new Set(),
};

/**
 * Two models are activeness-equivalent when they hold the same users with the
 * same active/idle state, so a re-render can be skipped when nothing changed.
 */
const sameActiveness = (a: OnlineUsersModel, b: OnlineUsersModel): boolean => {
  if (a.byId.size !== b.byId.size) {
    return false;
  }
  for (const [apiId, user] of a.byId) {
    if (b.byId.get(apiId)?.isActive !== user.isActive) {
      return false;
    }
  }
  return true;
};

/**
 * Subscribes to ActiveUsersChange events from the DataTable realtime stream and
 * returns the currently online users keyed by api id, in the order the server
 * provides (most-recently-engaged first — `Map` preserves insertion order).
 *
 * The user set and order are driven entirely by events. Activeness (`last_active`
 * within the last 60 seconds) is derived from the latest server timestamps both
 * when an event arrives and on a 10s interval, so a user that goes quiet without
 * a new event still transitions from active to idle. The interval re-render is
 * skipped unless a status actually flips, and the interval only runs while there
 * are online users.
 *
 * Each user is assigned a color from the avatar swatch on first sighting via
 * OKLCH farthest-point selection over the swatch indices already handed out. The
 * assignment persists for the lifetime of the hook, so a user that leaves and
 * rejoins keeps the same color.
 */
export const useOnlineUsers = (): OnlineUsersModel => {
  const { dataTableService } = useDataTable();
  const colorIndexByUser = useRef<Map<UserApiId, number>>(new Map());
  const usedColorIndices = useRef<Set<number>>(new Set());
  const onlineUsers = useRef<ReadonlyArray<OnlineUserSnapshot>>([]);
  const [model, setModel] = useState<OnlineUsersModel>(EMPTY_MODEL);

  const buildModel = useCallback((): OnlineUsersModel => {
    if (onlineUsers.current.length === 0) {
      return EMPTY_MODEL;
    }
    const activeThreshold = subSeconds(Date.now(), ACTIVE_WINDOW_SECONDS);
    const byId = new Map<UserApiId, OnlineUser>();
    const activeUserIds = new Set<UserApiId>();
    onlineUsers.current.forEach(({ apiId, lastActive }) => {
      const colorIndex = colorIndexByUser.current.get(apiId);
      if (colorIndex === undefined) {
        return;
      }
      const isActive = isAfter(new Date(lastActive), activeThreshold);
      byId.set(apiId, { apiId, color: avatarSwatch[colorIndex], isActive });
      if (isActive) {
        activeUserIds.add(apiId);
      }
    });
    return { byId, activeUserIds };
  }, []);

  useEffect(() => {
    const abortController = new AbortController();
    const handler = (event: DataTableActiveUsersChangeEvent): void => {
      onlineUsers.current = event.detail.users.map(({ api_id, last_active }) => {
        if (!colorIndexByUser.current.has(api_id)) {
          const colorIndex = pickFarthestAvatarColorIndex(usedColorIndices.current, api_id);
          colorIndexByUser.current.set(api_id, colorIndex);
          usedColorIndices.current.add(colorIndex);
        }
        return { apiId: api_id, lastActive: last_active };
      });
      setModel(buildModel());
    };
    dataTableService.subscribe(DataTableEvent.ActiveUsersChange, handler, abortController.signal);
    return () => {
      abortController.abort('teardown');
    };
  }, [dataTableService, buildModel]);

  const hasOnlineUsers = model.byId.size > 0;
  useEffect(() => {
    if (!hasOnlineUsers) {
      return;
    }
    const intervalId = setInterval(() => {
      setModel((current) => {
        const next = buildModel();
        return sameActiveness(current, next) ? current : next;
      });
    }, IDLE_REEVALUATION_INTERVAL_MS);
    return () => {
      clearInterval(intervalId);
    };
  }, [hasOnlineUsers, buildModel]);

  return model;
};
